import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '@/context/UserContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ChevronLeft, 
  ChevronRight, 
  BookOpen, 
  StickyNote,
  X,
  CheckCircle,
  XCircle,
  Trophy,
  RotateCcw,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COURSE_CONFIG = {
  memeglish: {
    name: 'MEMEGLISH',
    color: 'pump-green',
    bgColor: 'bg-pump-green/20'
  },
  memethematics: {
    name: 'MEMETHEMATICS',
    color: 'degen-purple',
    bgColor: 'bg-degen-purple/20'
  },
  memestory: {
    name: 'MEMESTORY',
    color: 'yellow-500',
    bgColor: 'bg-yellow-500/20'
  },
  memeart: {
    name: 'MEMEART',
    color: 'cyan-400',
    bgColor: 'bg-cyan-400/20'
  }
};

const CoursePage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, updateProgress, refreshUser } = useUser();
  
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [currentNote, setCurrentNote] = useState('');
  const [quizOpen, setQuizOpen] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);

  const config = COURSE_CONFIG[courseId];

  useEffect(() => {
    fetchContent();
    if (user) {
      fetchNotes();
      // Load saved progress
      const savedSlide = user.progress?.[courseId]?.current_slide || 0;
      setCurrentSlide(savedSlide);
    }
  }, [courseId, user]);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Failed to load course content');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/notes/${user.id}/${courseId}`);
      setNotes(response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const saveNote = async () => {
    if (!user || !currentNote.trim()) return;
    try {
      await axios.post(`${API}/notes`, {
        user_id: user.id,
        course_id: courseId,
        content: currentNote,
        slide_index: currentSlide
      });
      setCurrentNote('');
      fetchNotes();
      toast.success('Note saved!');
    } catch (error) {
      toast.error('Failed to save note');
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/notes/${noteId}`);
      fetchNotes();
      toast.success('Note deleted');
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const getSlides = () => {
    if (!content) return [];
    
    if (courseId === 'memeglish') {
      return content.terms?.map((term, idx) => ({
        type: 'term',
        title: term.term,
        content: term,
        slideNum: idx + 1
      })) || [];
    }
    
    if (courseId === 'memethematics') {
      return content.concepts?.map((concept, idx) => ({
        type: 'concept',
        title: concept.concept,
        content: concept,
        slideNum: idx + 1
      })) || [];
    }
    
    if (courseId === 'memestory') {
      return content.profiles?.map((profile, idx) => ({
        type: 'profile',
        title: profile.name,
        content: profile,
        slideNum: idx + 1
      })) || [];
    }
    
    if (courseId === 'memeart') {
      const slides = [];
      // Add explanation slides
      if (content.explanation) {
        slides.push({
          type: 'explanation',
          title: 'What is an NFT?',
          content: content.explanation,
          slideNum: 1
        });
      }
      // Add NFT slides
      content.nfts?.forEach((nft, idx) => {
        slides.push({
          type: 'nft',
          title: nft.name,
          content: nft,
          slideNum: idx + 2
        });
      });
      return slides;
    }
    
    return [];
  };

  const slides = getSlides();
  const totalSlides = slides.length;
  const progressPercent = totalSlides > 0 ? ((currentSlide + 1) / totalSlides) * 100 : 0;
  const isLastSlide = currentSlide === totalSlides - 1;

  const goToSlide = async (index) => {
    setCurrentSlide(index);
    if (user) {
      await updateProgress(courseId, { current_slide: index });
    }
  };

  const nextSlide = async () => {
    if (currentSlide < totalSlides - 1) {
      await goToSlide(currentSlide + 1);
    }
  };

  const prevSlide = async () => {
    if (currentSlide > 0) {
      await goToSlide(currentSlide - 1);
    }
  };

  const startQuiz = async () => {
    setQuizLoading(true);
    try {
      const response = await axios.get(`${API}/quiz/${courseId}`);
      setQuizQuestions(response.data.questions);
      setQuizAnswers(new Array(response.data.questions.length).fill(-1));
      setQuizResult(null);
      setQuizOpen(true);
    } catch (error) {
      toast.error('Failed to load quiz');
    } finally {
      setQuizLoading(false);
    }
  };

  const submitQuiz = async () => {
    if (quizAnswers.includes(-1)) {
      toast.error('Please answer all questions');
      return;
    }
    
    setQuizLoading(true);
    try {
      const response = await axios.post(`${API}/quiz/${courseId}/submit`, {
        user_id: user.id,
        course_id: courseId,
        answers: quizAnswers
      });
      setQuizResult(response.data);
      
      if (response.data.passed) {
        await refreshUser();
        toast.success('Congratulations! You passed! 🎉');
      } else {
        toast.error('You didn\'t pass. Try again!');
      }
    } catch (error) {
      toast.error('Failed to submit quiz');
    } finally {
      setQuizLoading(false);
    }
  };

  const resetQuiz = () => {
    setQuizResult(null);
    setQuizAnswers(new Array(quizQuestions.length).fill(-1));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-pump-green animate-spin" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-zinc-400">Course not found</p>
      </div>
    );
  }

  const currentSlideData = slides[currentSlide];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <div className="glass border-b border-white/10 sticky top-16 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/classes"
                className="p-2 rounded-lg hover:bg-white/5 text-zinc-400 hover:text-white transition-colors"
                data-testid="back-to-classes"
              >
                <ChevronLeft size={24} />
              </Link>
              <div>
                <h1 className={`font-unbounded font-bold text-xl text-${config.color}`} data-testid="course-title">
                  {config.name}
                </h1>
                <p className="text-sm text-zinc-500">
                  Slide {currentSlide + 1} of {totalSlides}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setNotesOpen(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-pump-surface border border-white/10 text-zinc-300 hover:text-white hover:border-pump-green/50 transition-all"
                data-testid="open-notes-btn"
              >
                <StickyNote size={18} />
                <span className="hidden sm:inline">Notes</span>
                {notes.length > 0 && (
                  <span className="w-5 h-5 rounded-full bg-pump-green text-black text-xs font-bold flex items-center justify-center">
                    {notes.length}
                  </span>
                )}
              </button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <Progress value={progressPercent} className="h-1.5 bg-pump-surface" />
          </div>
        </div>
      </div>

      {/* Slide Content */}
      <div className="flex-1 max-w-4xl mx-auto w-full px-4 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="glass rounded-2xl p-8 md:p-12"
            data-testid="slide-content"
          >
            {/* Term Slide */}
            {currentSlideData?.type === 'term' && (
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <span className="text-4xl">{currentSlideData.content.emoji}</span>
                  <h2 className="font-unbounded font-bold text-3xl md:text-4xl text-white">
                    {currentSlideData.content.term}
                  </h2>
                </div>
                <p className="text-xl text-zinc-300 leading-relaxed">
                  {currentSlideData.content.definition}
                </p>
                <div className="bg-pump-surface rounded-xl p-6 border border-white/10">
                  <p className="text-sm text-zinc-500 uppercase tracking-wider mb-2">Example</p>
                  <p className="text-zinc-300 italic">"{currentSlideData.content.example}"</p>
                </div>
              </div>
            )}

            {/* Concept Slide */}
            {currentSlideData?.type === 'concept' && (
              <div className="space-y-6">
                <h2 className="font-unbounded font-bold text-3xl md:text-4xl text-white">
                  {currentSlideData.content.concept}
                </h2>
                <p className="text-xl text-zinc-300 leading-relaxed">
                  {currentSlideData.content.definition}
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-pump-surface rounded-xl p-6 border border-white/10">
                    <p className="text-sm text-zinc-500 uppercase tracking-wider mb-2">Formula</p>
                    <p className="text-pump-green font-mono">{currentSlideData.content.formula}</p>
                  </div>
                  <div className="bg-pump-surface rounded-xl p-6 border border-white/10">
                    <p className="text-sm text-zinc-500 uppercase tracking-wider mb-2">Why it matters</p>
                    <p className="text-zinc-300">{currentSlideData.content.importance}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Profile Slide */}
            {currentSlideData?.type === 'profile' && (
              <div className="space-y-6">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="w-24 h-24 rounded-2xl overflow-hidden bg-gradient-to-br from-pump-green/30 to-degen-purple/30 shrink-0">
                    {currentSlideData.content.image ? (
                      <img 
                        src={currentSlideData.content.image} 
                        alt={currentSlideData.content.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="font-unbounded font-bold text-3xl text-white">
                          {currentSlideData.content.name[0]}
                        </span>
                      </div>
                    )}
                  </div>
                  <div>
                    <h2 className="font-unbounded font-bold text-3xl text-white">
                      {currentSlideData.content.name}
                    </h2>
                    <p className="text-pump-green font-medium">{currentSlideData.content.title}</p>
                  </div>
                </div>
                
                <p className="text-xl text-zinc-300 leading-relaxed">
                  {currentSlideData.content.bio}
                </p>
                
                <div className="bg-pump-surface rounded-xl p-6 border border-white/10">
                  <p className="text-sm text-zinc-500 uppercase tracking-wider mb-3">Achievements</p>
                  <ul className="space-y-2">
                    {currentSlideData.content.achievements?.map((achievement, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-zinc-300">
                        <CheckCircle className="text-pump-green shrink-0" size={16} />
                        {achievement}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <blockquote className="border-l-4 border-pump-green pl-4 py-2">
                  <p className="text-zinc-400 italic">"{currentSlideData.content.quote}"</p>
                </blockquote>
              </div>
            )}

            {/* NFT Explanation Slide */}
            {currentSlideData?.type === 'explanation' && (
              <div className="space-y-6">
                <h2 className="font-unbounded font-bold text-3xl md:text-4xl text-white">
                  What is an NFT?
                </h2>
                <p className="text-xl text-zinc-300 leading-relaxed">
                  {currentSlideData.content.what_is_nft}
                </p>
                
                <div className="bg-pump-surface rounded-xl p-6 border border-white/10">
                  <p className="text-sm text-zinc-500 uppercase tracking-wider mb-3">How it works</p>
                  <ol className="space-y-2">
                    {currentSlideData.content.how_it_works?.map((step, idx) => (
                      <li key={idx} className="text-zinc-300">{step}</li>
                    ))}
                  </ol>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {currentSlideData.content.key_concepts?.map((concept, idx) => (
                    <div key={idx} className="bg-pump-surface rounded-xl p-4 border border-white/10">
                      <p className="text-cyan-400 font-bold mb-1">{concept.term}</p>
                      <p className="text-sm text-zinc-400">{concept.definition}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* NFT Slide */}
            {currentSlideData?.type === 'nft' && (
              <div className="space-y-6">
                <div className="flex items-start justify-between">
                  <h2 className="font-unbounded font-bold text-2xl md:text-3xl text-white">
                    {currentSlideData.content.name}
                  </h2>
                  <span className="text-pump-green font-mono font-bold text-xl">
                    {currentSlideData.content.sale_price}
                  </span>
                </div>
                
                <p className="text-sm text-zinc-500">{currentSlideData.content.date}</p>
                
                <p className="text-lg text-zinc-300 leading-relaxed">
                  {currentSlideData.content.description}
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-pump-surface rounded-xl p-6 border border-white/10">
                    <p className="text-sm text-zinc-500 uppercase tracking-wider mb-2">How it worked</p>
                    <p className="text-zinc-300">{currentSlideData.content.how_it_works}</p>
                  </div>
                  <div className="bg-pump-surface rounded-xl p-6 border border-cyan-400/30">
                    <p className="text-sm text-cyan-400 uppercase tracking-wider mb-2">Significance</p>
                    <p className="text-zinc-300">{currentSlideData.content.significance}</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="glass border-t border-white/10 sticky bottom-0">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={prevSlide}
              disabled={currentSlide === 0}
              variant="ghost"
              className="text-zinc-400 hover:text-white disabled:opacity-30"
              data-testid="prev-slide-btn"
            >
              <ChevronLeft size={20} />
              <span className="hidden sm:inline ml-2">Previous</span>
            </Button>
            
            {/* Slide Dots */}
            <div className="hidden md:flex items-center gap-1.5 overflow-x-auto max-w-md px-4">
              {slides.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToSlide(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentSlide 
                      ? `bg-${config.color} w-6` 
                      : 'bg-zinc-600 hover:bg-zinc-500'
                  }`}
                />
              ))}
            </div>
            
            {isLastSlide ? (
              <Button
                onClick={startQuiz}
                disabled={quizLoading || !user}
                className="bg-pump-green text-black font-bold hover:shadow-neon-strong"
                data-testid="start-quiz-btn"
              >
                {quizLoading ? (
                  <Loader2 className="animate-spin mr-2" size={18} />
                ) : (
                  <Trophy className="mr-2" size={18} />
                )}
                Take Quiz
              </Button>
            ) : (
              <Button
                onClick={nextSlide}
                className="bg-pump-green text-black font-bold hover:shadow-neon-strong"
                data-testid="next-slide-btn"
              >
                <span className="hidden sm:inline mr-2">Next</span>
                <ChevronRight size={20} />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Notes Sidebar */}
      <AnimatePresence>
        {notesOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-[70]"
              onClick={() => setNotesOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-pump-surface border-l border-white/10 z-[80] flex flex-col"
              data-testid="notes-sidebar"
            >
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <h3 className="font-unbounded font-bold text-white">My Notes</h3>
                <button
                  onClick={() => setNotesOpen(false)}
                  className="p-2 rounded-lg hover:bg-white/5 text-zinc-400"
                  data-testid="close-notes-btn"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {notes.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No notes yet. Start taking notes!</p>
                ) : (
                  notes.map((note) => (
                    <div key={note.id} className="bg-pump-card rounded-lg p-4 border border-white/10 group">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-xs text-zinc-500">Slide {note.slide_index + 1}</span>
                        <button
                          onClick={() => deleteNote(note.id)}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/20 text-red-400 transition-all"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <p className="text-zinc-300 text-sm whitespace-pre-wrap">{note.content}</p>
                    </div>
                  ))
                )}
              </div>
              
              <div className="p-4 border-t border-white/10">
                <Textarea
                  value={currentNote}
                  onChange={(e) => setCurrentNote(e.target.value)}
                  placeholder="Write a note for this slide..."
                  className="bg-pump-card border-white/10 text-white placeholder:text-zinc-600 mb-3 min-h-[100px]"
                  data-testid="note-textarea"
                />
                <Button
                  onClick={saveNote}
                  disabled={!currentNote.trim() || !user}
                  className="w-full bg-pump-green text-black font-bold"
                  data-testid="save-note-btn"
                >
                  Save Note
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Quiz Modal */}
      <Dialog open={quizOpen} onOpenChange={setQuizOpen}>
        <DialogContent className="bg-pump-card border border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto z-[100]" data-testid="quiz-modal">
          <DialogHeader>
            <DialogTitle className="font-unbounded text-2xl text-white flex items-center gap-3">
              <Trophy className="text-pump-green" />
              {config.name} Quiz
            </DialogTitle>
          </DialogHeader>
          
          {quizResult ? (
            <div className="space-y-6 py-4">
              <div className={`text-center p-8 rounded-2xl ${quizResult.passed ? 'bg-pump-green/10 border border-pump-green/30' : 'bg-red-600/20 border border-red-500/50'}`}>
                {quizResult.passed ? (
                  <CheckCircle className="w-16 h-16 text-pump-green mx-auto mb-4" />
                ) : (
                  <XCircle className="w-20 h-20 text-red-500 mx-auto mb-4" />
                )}
                <h3 className={`font-unbounded font-black text-3xl mb-2 ${quizResult.passed ? 'text-pump-green' : 'text-red-500'}`}>
                  {quizResult.passed ? 'Congratulations!' : 'EXAM FAILED RETARD'}
                </h3>
                <p className="text-zinc-400">
                  You scored {quizResult.score} out of {quizResult.total}
                </p>
                {!quizResult.passed && (
                  <p className="text-red-400 text-sm mt-2">
                    Back to slide 1 for you.
                  </p>
                )}
              </div>
              
              {quizResult.passed ? (
                <Button
                  onClick={() => {
                    setQuizOpen(false);
                    navigate('/classes');
                  }}
                  className="w-full bg-pump-green text-black font-bold py-6"
                  data-testid="continue-btn"
                >
                  Continue to Classes
                </Button>
              ) : (
                <div className="space-y-4">
                  <Button
                    onClick={() => {
                      setQuizOpen(false);
                      goToSlide(0);
                    }}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-6"
                    data-testid="restart-course-btn"
                  >
                    <RotateCcw className="mr-2" size={18} />
                    Restart Course
                  </Button>
                  <p className="text-center text-zinc-500 text-sm">
                    You must review the material and try again from the beginning.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {quizQuestions.map((q, qIdx) => (
                <div key={qIdx} className="space-y-3">
                  <p className="font-medium text-white">
                    {qIdx + 1}. {q.question}
                  </p>
                  <div className="space-y-2">
                    {q.options.map((option, oIdx) => (
                      <button
                        key={oIdx}
                        onClick={() => {
                          const newAnswers = [...quizAnswers];
                          newAnswers[qIdx] = oIdx;
                          setQuizAnswers(newAnswers);
                        }}
                        className={`w-full text-left p-4 rounded-lg border transition-all ${
                          quizAnswers[qIdx] === oIdx
                            ? 'bg-pump-green/20 border-pump-green text-white'
                            : 'bg-pump-surface border-white/10 text-zinc-300 hover:border-white/30'
                        }`}
                        data-testid={`quiz-option-${qIdx}-${oIdx}`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              
              <Button
                onClick={submitQuiz}
                disabled={quizLoading || quizAnswers.includes(-1)}
                className="w-full bg-pump-green text-black font-bold py-6"
                data-testid="submit-quiz-btn"
              >
                {quizLoading ? (
                  <Loader2 className="animate-spin mr-2" size={18} />
                ) : null}
                Submit Answers
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CoursePage;
